// ============================================================
// src/hooks/useAuth.js
//
// ── WHAT THIS FILE DOES ──────────────────────────────────────
// This is the useAuth HOOK — a custom React hook that wraps
// Supabase's authentication system.
//
// It is the single source of truth for "who is logged in?"
// across the entire app. Every component that needs to know
// whether a user is authenticated imports this hook.
//
// ── WHAT IT PROVIDES ─────────────────────────────────────────
//   user          – the current Supabase user object, or null
//   loading       – true while the initial session check runs
//   signIn        – signs in with email + password
//   signUp        – creates a new account
//   signOut       – ends the current session
//   updateProfile – updates the user's metadata / profile fields
//   error         – last auth error message, or null
//
// ── HOW THE SESSION LISTENER WORKS ───────────────────────────
// Supabase fires onAuthStateChange whenever:
//   • The page first loads (gives us the stored session)
//   • The user signs in or signs up
//   • The user signs out
//   • The session token refreshes automatically
//
// We subscribe to that event in useEffect (runs once on mount)
// and unsubscribe in the cleanup function to avoid memory leaks.
//
// ── EXPORTS ──────────────────────────────────────────────────
// Named export only:  export function useAuth()
// All import sites must use:  import { useAuth } from '../hooks/useAuth'
// ============================================================

import { useState, useEffect } from 'react'
import { supabase } from '../lib/supabaseClient'
// supabase — the configured Supabase JS client used for all auth calls


// ============================================================
// useAuth HOOK
// ============================================================

export function useAuth() {

  // ── STATE ─────────────────────────────────────────────────

  const [user, setUser] = useState(null)
  // null    = not logged in (or session check not yet complete)
  // object  = the logged-in Supabase user
  //           { id, email, user_metadata: { performer_name, ... }, ... }

  const [loading, setLoading] = useState(true)
  // true  = we haven't yet checked whether a session exists.
  //         App.js shows "Loading..." instead of flashing Login.
  // false = session check is done — render the appropriate UI.

  const [error, setError] = useState(null)
  // null   = no error
  // string = the last error message from a sign-in / sign-up attempt


  // ── SESSION LISTENER ─────────────────────────────────────

  useEffect(() => {
    // Step 1: get the current session immediately on mount.
    // This handles the case where the user refreshes the page —
    // Supabase stores the session in localStorage so we can restore it.
    supabase.auth.getSession().then(({ data: { session } }) => {
      setUser(session?.user ?? null)
      // session?.user = the user if a session exists, undefined if not
      // ?? null       = fall back to null so state is never undefined
      setLoading(false)
      // session check is done — stop showing the loading screen
    })

    // Step 2: subscribe to future auth changes (sign in, sign out, refresh).
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (_event, session) => {
        setUser(session?.user ?? null)
        // keep user state in sync with whatever Supabase tells us
        setLoading(false)
        // in case loading was still true (e.g. signup callback)
      }
    )

    // Step 3: clean up the listener when the component unmounts.
    // Without this, the listener keeps running even after React
    // unmounts the component, causing a memory leak and stale state updates.
    return () => subscription.unsubscribe()

  }, [])
  // [] = run this effect only once, when the hook first mounts.
  // The auth state listener itself handles all future updates.


  // ── AUTH METHODS ──────────────────────────────────────────

  // signIn — authenticates an existing user with email + password
  const signIn = async (email, password) => {
    setError(null)
    // clear any previous error before attempting

    const { error: authError } = await supabase.auth.signInWithPassword({
      email: email.trim(),
      // .trim() removes accidental leading/trailing whitespace
      password,
    })

    if (authError) {
      setError(authError.message)
      throw authError
      // re-throw so the calling component can react (e.g. show an error banner)
    }
  }


  // signUp — creates a new account
  // performerName is stored in user_metadata so it's available instantly
  // without a separate database query
  const signUp = async (email, password, performerName) => {
    setError(null)

    const { error: authError } = await supabase.auth.signUp({
      email: email.trim(),
      password,
      options: {
        data: {
          performer_name: performerName?.trim() || null,
          // performer_name in user_metadata = shown on the dashboard
          // null if left blank rather than storing an empty string
        },
      },
    })

    if (authError) {
      setError(authError.message)
      throw authError
    }
  }


  // signOut — ends the current Supabase session
  // After this runs, onAuthStateChange fires with session=null,
  // which sets user to null and causes App.js to render <Login />.
  const signOut = async () => {
    setError(null)

    const { error: authError } = await supabase.auth.signOut()

    if (authError) {
      setError(authError.message)
      throw authError
    }
  }


  // updateProfile — saves changes to user metadata
  // Called from the Settings page when the user edits their profile.
  // updates = { performer_name, pronouns, location, ... }
  const updateProfile = async (updates) => {
    setError(null)

    const { error: updateError } = await supabase.auth.updateUser({
      data: updates,
      // Supabase merges these into user.user_metadata automatically
      // so we only need to pass the changed fields
    })

    if (updateError) {
      setError(updateError.message)
      throw updateError
    }
  }


  // ── RETURN ────────────────────────────────────────────────
  // Expose everything the rest of the app needs

  return {
    user,            // Supabase user object or null
    loading,         // true while session is being checked on first load
    error,           // last error message string or null
    signIn,          // (email, password) => Promise<void>
    signUp,          // (email, password, performerName) => Promise<void>
    signOut,         // () => Promise<void>
    updateProfile,   // (updates: object) => Promise<void>
  }
}